import {
  OrderSide,
  TimeInForce,
  OrderType,
  TriggerType,
  KatanaPerpsOrderEventDataGeneral,
} from '@katanaperps/katana-perps-sdk';

import { ApiOrder } from 'api/types';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { formatNumber, formatUsdWithSign } from 'lib/display/formatting';
import { isConsideredEmptyOrZero } from 'lib/numbers';
import { capitalizeFirstLetter } from 'lib/string';

/**
 * Similar to `identifyTPSLError` it will detect incorrectly re-placed order
 * and display error for instant execution.
 */
export function checkOrderCrossingSpread({
  side,
  price,
  midOrLastPrice,
}: {
  side: 'buy' | 'sell';
  price: number;
  midOrLastPrice: string | number;
}) {
  const priceAboveCurrentPrice = price > Number(midOrLastPrice);
  return !price
    ? false
    : side === 'buy'
    ? priceAboveCurrentPrice
    : !priceAboveCurrentPrice;
}

/** Chart dragging functionality for repricing - Reduce only order */
export function checkStopOrderInstantExecutionError({
  type,
  price,
  positionIsShort,
  currentPrice,
}: {
  type: 'takeProfit' | 'stopLoss';
  price: number;
  positionIsShort: boolean;
  currentPrice: number | string;
}) {
  const currentPriceNum = Number(currentPrice);
  const priceEqualCurrentPrice = price === currentPriceNum;
  const priceAboveCurrentPrice = price > currentPriceNum;
  if (type === 'takeProfit') {
    const takeProfitError = !price
      ? false
      : priceEqualCurrentPrice
      ? true
      : positionIsShort
      ? priceAboveCurrentPrice
      : !priceAboveCurrentPrice;
    return takeProfitError;
  }
  const stopLossError = !price
    ? false
    : priceEqualCurrentPrice
    ? true
    : positionIsShort
    ? !priceAboveCurrentPrice
    : priceAboveCurrentPrice;
  return stopLossError;
}

export const getOrderQuantityWithSymbol = (
  order: ApiOrder,
  decimals: number = EXCHANGE_DECIMALS,
  maximumPositionSize: string | undefined
): string | null => {
  if (order.originalQuantity === maximumPositionSize) {
    // TPSL close position order
    return 'Close Position';
  }
  return `${formatNumber(order.originalQuantity, {
    fixedDecimals: decimals,
  })} ${order.baseSymbol}`;
};

export const getOrderTotalInQuoteWithSymbol = (
  order: ApiOrder,
  /** Detect maximum position size - Mobile Only */
  maximumPositionSize: string | undefined | false
): string | null => {
  if (order.originalQuantity === maximumPositionSize) {
    // TPSL close position order
    return 'Close Position';
  }
  return order.originalQuantity && order.price
    ? formatUsdWithSign(Number(order.originalQuantity) * Number(order.price))
    : null;
};

export const getOrderExecutedQuantityWithSymbol = (
  order: ApiOrder,
  decimals: number = EXCHANGE_DECIMALS
): string => {
  return `${formatNumber(order.executedQuantity, {
    fixedDecimals: decimals,
  })} ${order.baseSymbol}`;
};

export const isMarketOrder = (orderType: OrderType): boolean => {
  return (
    orderType === OrderType.market ||
    orderType === OrderType.stopLossMarket ||
    orderType === OrderType.takeProfitMarket
  );
};

export const getReadableOrderType = (orderType?: ApiOrder['type']) => {
  if (!orderType) {
    return '';
  }
  return orderType === 'stopLossLimit' || orderType === 'takeProfitLimit'
    ? 'Stop Limit'
    : orderType === 'stopLossMarket' || orderType === 'takeProfitMarket'
    ? 'Stop Market'
    : capitalizeFirstLetter(orderType);
};

export const getOrderTriggerExplanation = (
  order: Pick<ApiOrder, 'triggerPrice' | 'triggerType' | 'type' | 'side'>,
  tickSizeDecimals: number | undefined
): string => {
  const { triggerPrice, type } = order;
  if (
    !triggerPrice ||
    !type ||
    type === OrderType.market ||
    type === OrderType.limit
  ) {
    return '';
  }
  if (type === OrderType.trailingStopMarket) {
    // Not yet supported
    return 'TrailingMarketStop';
  }
  // If BUY and (stopLossLimit or stopLossMarket)
  //  then MARKET_PRICE >= STOP
  //
  // If BUY and (takeProfitLimit or takeProfitMarket)
  //  then MARKET_PRICE <= STOP
  //
  // If SELL and (stopLossLimit or stopLossMarket)
  //  then MARKET_PRICE <= STOP
  //
  // If SELL and (takeProfitLimit or takeProfitMarket)
  //  then MARKET_PRICE >= STOP

  const isTake =
    order.type === OrderType.takeProfitLimit ||
    order.type === OrderType.takeProfitMarket;
  const isStop =
    order.type === OrderType.stopLossLimit ||
    order.type === OrderType.stopLossMarket;
  const sign =
    (order.side === OrderSide.buy && isTake) ||
    (order.side === OrderSide.sell && isStop)
      ? '<='
      : '>=';

  return `${capitalizeFirstLetter(order.triggerType)} ${sign} ${formatNumber(
    triggerPrice,
    { fixedDecimals: tickSizeDecimals ?? EXCHANGE_DECIMALS }
  )}`;
};

/** Simple calculation of mid price (use on all places for simple search) */
export function middleOrderbookPrice(
  minAsk: string | number,
  maxtBid: string | number
) {
  return (Number(maxtBid) + Number(minAsk)) / 2;
}

/**
 * Determine if trigger it takeProfit or stopLoss.
 *
 *  Stop Loss     Sell    Current price <= stop (trigger) price
 *  Stop Loss     Buy     Current price >= stop (trigger) price
 *  Take Profit   Sell    Current price >= stop (trigger) price
 *  Take Profit   Buy     Current price <= stop (trigger) price
 */
// TODO: not using Last/Index price but only orderbook
export const isTriggerTakeProfit = ({
  side,
  indexPrice,
  bestBid,
  bestAsk,
  triggerPrice,
  triggerType,
}: {
  side: OrderSide;
  indexPrice: string | undefined;
  bestBid: string | undefined;
  bestAsk: string | undefined;
  triggerPrice: number;
  triggerType: Extract<TriggerType, 'last' | 'index'>;
}): boolean => {
  if (
    (triggerType === 'last' &&
      (!bestBid ||
        !bestAsk ||
        isConsideredEmptyOrZero(bestBid) ||
        isConsideredEmptyOrZero(bestAsk))) ||
    (triggerType === 'index' &&
      (!indexPrice || isConsideredEmptyOrZero(indexPrice)))
  ) {
    throw new Error('Missing index price or empty orderbook');
  }
  if (!triggerPrice) {
    throw new Error('Missing stop price');
  }
  if (!bestAsk || !bestBid) {
    throw new Error('Missing orderbook data');
  }
  const targetPrice =
    triggerType === 'index'
      ? Number(indexPrice)
      : // TODO: verify we shouldn't be bid/ask separatelly for Buy and Sell
        middleOrderbookPrice(bestAsk, bestBid);
  return (
    (side === OrderSide.sell && triggerPrice >= targetPrice) ||
    (side === OrderSide.buy && triggerPrice <= targetPrice)
  );
};

export const isOrderPostOnly = (order: Pick<ApiOrder, 'timeInForce'>) =>
  order.timeInForce === TimeInForce.gtx;

export const determineStopType = (orderType: OrderType) => {
  return orderType === 'takeProfitLimit' || orderType === 'takeProfitMarket'
    ? 'takeProfit'
    : orderType === 'stopLossLimit' || orderType === 'stopLossMarket'
    ? 'stopLoss'
    : undefined;
};

export const isTPSLOrder = (
  order: ApiOrder | KatanaPerpsOrderEventDataGeneral,
  maximumOrderSize: string
) => {
  return order.originalQuantity === maximumOrderSize;
};
